import { createSlice } from "@reduxjs/toolkit";
import { UnitDisplay, DisclaimerType } from "const";
import { mergeObj } from "utils/common-util";
import compact from "lodash/compact";
import union from "lodash/union";
import { handleErrorStopLoading, handleLoading, handleFail } from "redux/redux-util";
import update from "immutability-helper";

const initialSetting = {
  unitDisplay: UnitDisplay.UNIT, // unit || baht
};

const verifyReceiveAccountInitial = {
  verifying: false,
  isAccount: false,
  accountNo: null,
};

const initialState = {
  loading: false,
  subscriptionDetail: {},
  setting: { ...initialSetting },
  fetchConfirmationInfoDone: false,
  sendFirstOTP: false,
  profileRegister: {},
  verifyReceiveAccount: { ...verifyReceiveAccountInitial },
  error: null,
};

export const buildAcceptDiscList = (currentAcceptDiscList = [], detailPayload = {}) => {
  const { acceptDisc04, acceptDisc07, acceptRiskDocument, acceptCrucialRiskDocument } = detailPayload;

  const hasAcceptDiscFlag =
    acceptDisc04 !== undefined ||
    acceptDisc07 !== undefined ||
    acceptRiskDocument !== undefined ||
    acceptCrucialRiskDocument !== undefined;

  if (!hasAcceptDiscFlag) {
    return currentAcceptDiscList;
  }

  // do not care falsy value, right process will must accepted all of this
  return compact(
    union(currentAcceptDiscList, [
      acceptDisc04 && DisclaimerType.AcceptWarningRisk,
      acceptDisc07 && DisclaimerType.ConfirmPersonalInfo,
      acceptRiskDocument && DisclaimerType.RiskDocument,
      acceptCrucialRiskDocument && DisclaimerType.CRUCIAL_RISK_DOC,
    ]),
  );
};

const slice = createSlice({
  initialState,
  name: "subscription",
  reducers: {
    initSubscription: (_, action) => {
      return {
        ...initialState,
        subscriptionDetail: { ...action.payload },
        fetchConfirmationInfoDone: false,
        sendFirstOTP: false,
      };
    },
    subscriptionDetailChange: (state, action) => {
      const {
        acceptDisc04,
        acceptDisc07,
        acceptRiskDocument,
        acceptCrucialRiskDocument,
        ...payload
      } = action.payload;

      const acceptDiscList = buildAcceptDiscList(state.subscriptionDetail.acceptDiscList, {
        acceptDisc04,
        acceptDisc07,
        acceptRiskDocument,
        acceptCrucialRiskDocument,
      });

      payload.acceptDiscList = acceptDiscList;
      state.subscriptionDetail = mergeObj(state.subscriptionDetail, payload);
    },
    changeUnitDisplaySetting: (state, action) => {
      state.setting.unitDisplay = action.payload;
    },
    getConfirmationInfo: handleLoading,
    getConfirmationInfoSuccess: (state, action) => {
      state.loading = false;
      state.subscriptionDetail = mergeObj(state.subscriptionDetail, action.payload);
      state.fetchConfirmationInfoDone = true;
    },
    getConfirmationInfoFailure: handleErrorStopLoading,
    sendOTP: () => {},
    sendOTPSuccess: (state, action) => {
      state.subscriptionDetail.refOtp = action.payload;
      state.sendFirstOTP = true;
    },
    sendOTPFailure: handleFail,

    reSendOTP: () => {},
    reSendOTPSuccess: (state, action) => {
      state.subscriptionDetail.refOtp = action.payload;
    },
    reSendOTPFailure: handleFail,

    confirmPayment: handleLoading,
    confirmPaymentSuccess: (state, action) => {
      state.loading = false;
      state.subscriptionDetail = {
        ...state.subscriptionDetail,
        ...(action.payload ?? {}),
      };
    },
    confirmPaymentFailure: handleErrorStopLoading,
    verifyReceiveAccount: (state, { payload }) => {
      state.verifyReceiveAccount.verifying = true;
      state.verifyReceiveAccount.accountNo = payload.accountNo;
    },
    verifyReceiveAccountSuccess: (state, { payload }) => {
      state.verifyReceiveAccount.verifying = false;
      state.verifyReceiveAccount.isAccount = payload.isAccount;
    },
    verifyReceiveAccountFailure: (state, action) => {
      state.verifyReceiveAccount.verifying = false;
      state.verifyReceiveAccount.accountNo = null;
    },
    updateKnowledgeAssetmentAnswer: (state, { payload }) => {
      return update(state, {
        subscriptionDetail: {
          $merge: {
            ka: payload,
          },
        },
      });
    },
    checkPreApprove: (state) => {
      state.loading = true;
      state.subscriptionDetail.useLombard = true;
    },
    checkPreApproveSuccess: (state, { payload }) => {
      state.loading = false;
      state.subscriptionDetail.unit = payload.unit;
      state.subscriptionDetail.baht = payload.baht;
    },
    checkPreApproveFailure: (state) => {
      state.loading = false;
    },
    manualSetUnitAndBaht: (state, { payload }) => {
      state.subscriptionDetail.unit = payload.unit;
      state.subscriptionDetail.baht = payload.baht;
    },
    clearUseLombard: (state) => {
      state.subscriptionDetail.useLombard = false;
    },
    clearConfirmationInfo: (state) => {
      state.fetchConfirmationInfoDone = false;
      state.subscriptionDetail.receiveMethod = null;
    },
    clearSelectedAccount: (state) => {
      state.subscriptionDetail.accountNo = null;
      state.subscriptionDetail.rfdAccountNo = null;

      for (let i = 1; i < 10; i++) {
        if (state.subscriptionDetail[`rfdAccountNoCcy${i}`]) {
          state.subscriptionDetail[`rfdAccountNoCcy${i}`] = null;
        } else {
          break;
        }
      }
    },
    resetError: (state) => {
      state.error = null;
    },
    acceptAutoRedeemAccount: (state, action) => {
      state.subscriptionDetail.acceptAutoRedeemAccount = action.payload;
    },
    receiveConsent: (state, action) => {
      const { code, acceptId } = action.payload;
      if (!state.subscriptionDetail.disclaimerMapping) {
        state.subscriptionDetail.disclaimerMapping = {};
      }
      state.subscriptionDetail.disclaimerMapping[code] = acceptId;
    },
  },
});

export const {
  initSubscription,
  subscriptionDetailChange,
  changeUnitDisplaySetting,

  getConfirmationInfo,
  getConfirmationInfoSuccess,
  getConfirmationInfoFailure,

  sendOTP,
  sendOTPSuccess,
  sendOTPFailure,
  reSendOTP,
  reSendOTPSuccess,
  reSendOTPFailure,

  confirmPayment,
  confirmPaymentSuccess,
  confirmPaymentFailure,

  verifyReceiveAccount,
  verifyReceiveAccountSuccess,
  verifyReceiveAccountFailure,

  updateKnowledgeAssetmentAnswer,

  checkPreApprove,
  checkPreApproveSuccess,
  checkPreApproveFailure,
  manualSetUnitAndBaht,

  clearUseLombard,
  clearSelectedAccount,
  clearConfirmationInfo,

  resetError,
  acceptAutoRedeemAccount,
  receiveConsent,
} = slice.actions;

export default slice.reducer;
